import { objectsAreEqual } from '@/utils/objectUtils';
/**
 * Константы для ограничения масштаба
 */
const MIN_ZOOM = 0.01;
const MAX_ZOOM = 20;
/**
 * Ограничивает значение масштаба в допустимых пределах
 *
 * @param value - Значение масштаба
 * @returns Ограниченное значение масштаба
 */
export function clampZoom(value) {
    return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, value));
}
/**
 * Создает глубокую копию объекта через JSON сериализацию
 *
 * @param obj - Объект для клонирования
 * @returns Копия объекта
 */
export function clone(obj) {
    return JSON.parse(JSON.stringify(obj));
}
/**
 * Собирает все элементы из всех слоев в Map по ID
 *
 * @param state - Состояние доски
 * @returns Map элементов по ID
 */
export function collectElements(state) {
    const map = new Map();
    for (const layer of state.layers || []) {
        for (const element of layer.elements || []) {
            map.set(element.id, element);
        }
    }
    return map;
}
/**
 * Добавляет новый элемент на доску
 *
 * @param state - Текущее состояние доски
 * @param layerId - ID слоя, в который добавляется элемент
 * @param el - Данные нового элемента
 * @returns Обновленное состояние доски, флаг fromUndo и ID пользователя
 */
export function addElement(state, layerId, el) {
    var _a, _b, _c;
    // Если есть replaceElementId — удаляем элемент с таким id
    if (typeof el.replaceElementId === 'number') {
        for (const layer of state.layers || []) {
            layer.elements = (layer.elements || []).filter((element) => element.id !== el.replaceElementId);
        }
    }
    const layer = (state.layers || []).find((l) => l.id === layerId);
    if (!layer)
        return { draft: state, fromUndo: el.fromUndo || false, userId: el.initiatorId || null };
    const newElement = Object.assign({ id: (_a = el.id) !== null && _a !== void 0 ? _a : Date.now(), name: el.name || '', description: el.description, layerId: layerId, elementType: (_b = el.elementType) !== null && _b !== void 0 ? _b : 'token', note: el.note, props: (_c = el.props) !== null && _c !== void 0 ? _c : {}, locked: el.locked || false, isPresentationFrame: el.isPresentationFrame || false, isActivePresentationFrame: el.isActivePresentationFrame || false, presentationOrder: el.presentationOrder || 0, zIndex: el.zIndex || 0 }, (el.asset ? { asset: el.asset } : {}));
    layer.elements = [...(layer.elements || []), newElement];
    return { draft: state, fromUndo: el.fromUndo || false, userId: el.initiatorId || null };
}
/**
 * Обновляет свойства элементов на доске
 *
 * @param state - Текущее состояние доски
 * @param payload - Массив элементов с обновлениями
 * @param shallow - Если true, создает копию состояния перед изменением
 * @returns Обновленное состояние доски, флаг fromUndo и ID пользователя
 */
export function patchElement(state, payload, shallow) {
    const draft = shallow ? clone(state) : state;
    let fromUndo = false;
    let userId = null;
    for (const patch of payload) {
        if (patch.fromUndo) {
            fromUndo = true;
        }
        if (!userId && patch.initiatorId) {
            userId = patch.initiatorId;
        }
        for (const layer of draft.layers || []) {
            for (const element of layer.elements || []) {
                if (element.id === patch.id) {
                    Object.assign(element, patch);
                    // Синхронизируем props.zIndex с el.zIndex если zIndex был обновлен
                    if (patch.zIndex !== undefined) {
                        element.props = Object.assign(Object.assign({}, element.props), { zIndex: patch.zIndex });
                    }
                }
            }
        }
    }
    return { draft, fromUndo, userId };
}
/**
 * Удаляет элементы с доски
 *
 * @param state - Текущее состояние доски
 * @param payload - Массив элементов для удаления (достаточно указать id)
 * @param shallow - Если true, создает копию состояния перед изменением
 * @returns Обновленное состояние доски, флаг fromUndo и ID пользователя
 */
export function deleteElement(state, payload, shallow) {
    const draft = shallow ? clone(state) : state;
    let fromUndo = false;
    let userId = null;
    for (const layer of draft.layers || []) {
        layer.elements = (layer.elements || []).filter((element) => {
            if (element.fromUndo) {
                fromUndo = true;
            }
            if (!userId && element.initiatorId) {
                userId = element.initiatorId || null;
            }
            return !payload.some((p) => p.id === element.id);
        });
    }
    return { draft, fromUndo, userId };
}
/**
 * Обновляет свойства слоя на доске
 *
 * @param state - Текущее состояние доски
 * @param id - ID слоя для обновления
 * @param patch - Обновления для слоя
 * @returns Обновленное состояние доски
 */
export function patchLayer(state, id, patch) {
    for (const layer of state.layers || []) {
        if (layer.id === id)
            Object.assign(layer, patch);
    }
    return { draft: state, fromUndo: false, userId: null };
}
/**
 * Находит первый видимый слой на доске
 *
 * @param state - Состояние доски
 * @returns Первый видимый слой или undefined
 */
export function firstVisibleLayer(state) {
    return (state.layers || []).find((layer) => layer.visible);
}
/**
 * Определяет состояние блокировки для выделенных элементов
 *
 * @param state - Состояние доски
 * @param ids - Массив ID выделенных элементов
 * @returns true если все заблокированы, false если все разблокированы, null если смешанное состояние
 */
export function selectionLockState(state, ids) {
    if (!ids.length)
        return null;
    const elements = ids
        .map((id) => {
        for (const layer of state.layers || []) {
            for (const element of layer.elements || []) {
                if (element.id === id)
                    return { element, layer };
            }
        }
        return null;
    })
        .filter(Boolean);
    const lockedStates = elements.map(({ element, layer }) => !!(element.locked || layer.locked));
    const allTrue = lockedStates.every((v) => v);
    const allFalse = lockedStates.every((v) => !v);
    return allTrue ? true : allFalse ? false : null;
}
// Собираем элементы в Map по id (если один id встречается в нескольких слоях — возьмётся последний)
const elementsByIdMap = (state) => {
    const map = new Map();
    for (const layer of state.layers || []) {
        for (const el of layer.elements || []) {
            map.set(el.id, el);
        }
    }
    return map;
};
/**
 * Вычисляет различия между двумя состояниями доски
 *
 * @param before - Предыдущее состояние доски
 * @param after - Новое состояние доски
 * @returns Объект с добавленными, удаленными и обновленными элементами
 */
export function diffElements(before, after) {
    const beforeMap = elementsByIdMap(before);
    const afterMap = elementsByIdMap(after);
    const ids = new Set([...beforeMap.keys(), ...afterMap.keys()]);
    const added = [];
    const removed = [];
    const updated = [];
    for (const id of ids) {
        const prev = beforeMap.get(id);
        const next = afterMap.get(id);
        if (!prev && next) {
            // новый элемент
            added.push(next);
            continue;
        }
        if (prev && !next) {
            // элемент удалён
            removed.push(prev);
            continue;
        }
        if (prev && next && !objectsAreEqual(prev, next)) {
            // элемент остался, но изменился (id совпал)
            updated.push({ before: prev, after: next });
        }
    }
    return { added, removed, updated };
}
/**
 * Отправляет изменения истории на сервер
 *
 * @param added - Массив добавленных элементов
 * @param removed - Массив удаленных элементов
 * @param updated - Массив обновленных элементов (до и после)
 * @param sendMessage - Функция для отправки сообщений на сервер
 */
export function sendHistoryStates(added, removed, updated, sendMessage) {
    // Отправляем обновлённые элементы
    updated.map(({ before, after }) => {
        sendMessage({
            type: 'element.update',
            payload: [{ id: after.id, props: after.props, fromUndo: true }],
        });
    });
    // Отправляем добавленные элементы
    added.map((el) => {
        sendMessage({
            type: 'element.add',
            payload: {
                layerId: el.layerId,
                elementType: el.elementType,
                asset: el.asset || {},
                props: el.props,
                fromUndo: true,
            },
        });
    });
    // Отправляем удалённые элементы
    removed.map((el) => {
        sendMessage({ type: 'element.delete', payload: [{ id: el.id, fromUndo: true }] });
    });
}
/**
 * Генерирует уникальный ID группы
 *
 * @returns Уникальный ID группы
 */
export function generateGroupId() {
    return `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
/**
 * Получает все элементы группы по ID группы
 *
 * @param state - Состояние доски
 * @param groupId - ID группы
 * @returns Массив элементов группы
 */
export function getGroupElements(state, groupId) {
    var _a;
    const elements = [];
    for (const layer of state.layers || []) {
        for (const element of layer.elements || []) {
            if (((_a = element.props) === null || _a === void 0 ? void 0 : _a.groupId) === groupId) {
                elements.push(element);
            }
        }
    }
    return elements;
}
/**
 * Получает ID группы элемента
 *
 * @param element - Элемент доски
 * @returns ID группы или null
 */
export function getElementGroupId(element) {
    var _a;
    return ((_a = element.props) === null || _a === void 0 ? void 0 : _a.groupId) || null;
}
/**
 * Получает все элементы, которые находятся в тех же группах, что и указанные элементы
 *
 * @param state - Состояние доски
 * @param elementIds - Массив ID элементов
 * @returns Set ID всех элементов из всех групп
 */
export function getAllGroupElementIds(state, elementIds) {
    const result = new Set(elementIds);
    const elementsMap = collectElements(state);
    for (const id of elementIds) {
        const element = elementsMap.get(id);
        if (!element)
            continue;
        const groupId = getElementGroupId(element);
        if (!groupId)
            continue;
        const groupElements = getGroupElements(state, groupId);
        for (const groupElement of groupElements) {
            result.add(groupElement.id);
        }
    }
    return result;
}
